/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react';
import { useQueryParams, ArrayParam, withDefault } from 'use-query-params';

import { connectorTypeFilterStyles } from './connector-type-filter.styles';
import SelectedValue from '../selected-value/selected-value.component';
import chevronRightIcon from '../../../../../assets/chevron-right.svg';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocationsParams } from '../../../../../store/locations/locations-list.selector';
import { setLocationsParamsConnectorTypes } from '../../../../../store/locations/locations-list.slice';
import {
    EConnectorType,
    getConnectorTypes,
    normalizeConnectorType,
} from '../../../../set-up/utils/getConnectorTypes';
import { useIntl } from 'react-intl';

const allConnectors = getConnectorTypes(16);

const ConnectorsFiltersParam = withDefault(ArrayParam, []);

const ConnectorTypeFilter = () => {
    const [isOpen, setIsOpen] = useState(false);
    const intl = useIntl();
    const { connectorTypes } = useSelector(selectLocationsParams);

    const [query, setQuery] = useQueryParams({
        connectors: ConnectorsFiltersParam,
    });

    const dispatch = useDispatch();

    const onAddConnectorType = useCallback(
        (connectorType: EConnectorType) => {
            dispatch(setLocationsParamsConnectorTypes([...connectorTypes, connectorType]));
            setQuery({ connectors: [...query.connectors, connectorType] });
        },
        [connectorTypes]
    );

    const onRemoveConnectorType = useCallback(
        (connectorType: EConnectorType) => {
            const filteredConnectorTypes = connectorTypes.filter(type => type !== connectorType);
            dispatch(setLocationsParamsConnectorTypes(filteredConnectorTypes));
            setQuery({ connectors: filteredConnectorTypes });
        },
        [connectorTypes]
    );

    const { selectedConnectors, unselectedConnectors } = useMemo(() => {
        let selectedConnectors: EConnectorType[] = [];
        let unselectedConnectors: EConnectorType[] = [];
        const normalizedConnectorTypes = new Set(connectorTypes.map(type => normalizeConnectorType(type)));

        if (connectorTypes.length > 0) {
            selectedConnectors = Object.keys(allConnectors).filter(key =>
                normalizedConnectorTypes.has(key)
            ) as EConnectorType[];
            unselectedConnectors = Object.keys(allConnectors).filter(
                key => !normalizedConnectorTypes.has(key)
            ) as EConnectorType[];
        } else {
            unselectedConnectors = Object.keys(allConnectors) as EConnectorType[];
        }

        return { selectedConnectors, unselectedConnectors };
    }, [connectorTypes]);

    const selectedConnectorItems = useMemo(() => {
        if (selectedConnectors.length === 0) {
            return <SelectedValue value="All" />;
        }

        return selectedConnectors.map(connector => (
            <SelectedValue
                key={connector}
                value={allConnectors[connector]?.name || connector}
                onRemove={() => onRemoveConnectorType(connector)}
                icon={allConnectors[connector]?.icon}
            />
        ));
    }, [selectedConnectors]);

    const unselectedConnectorItems = useMemo(() => {
        return unselectedConnectors.map(connector => (
            <SelectedValue
                isSelected={false}
                key={connector}
                value={allConnectors[connector]?.name || connector}
                icon={allConnectors[connector]?.icon}
                onSelect={() => onAddConnectorType(connector)}
            />
        ));
    }, [unselectedConnectors]);

    return (
        <div css={connectorTypeFilterStyles.root}>
            <div css={connectorTypeFilterStyles.topBlock}>
                <div css={connectorTypeFilterStyles.headerWrapper}>
                    <span css={connectorTypeFilterStyles.title}>{intl.formatMessage({ id: 'connectorTypeFilterLabel' })}</span>
                    <div css={connectorTypeFilterStyles.connectorsWrapper}>
                        {selectedConnectorItems}
                    </div>
                </div>
                <img
                    css={
                        isOpen
                            ? [
                                  connectorTypeFilterStyles.chevronIcon,
                                  connectorTypeFilterStyles.chevronIconOpen,
                              ]
                            : connectorTypeFilterStyles.chevronIcon
                    }
                    src={chevronRightIcon}
                    alt="chevron-right"
                    onClick={() => setIsOpen(!isOpen)}
                />
            </div>
            {isOpen && (
                <div css={connectorTypeFilterStyles.contentWrapper}>
                    <div css={connectorTypeFilterStyles.connectorsWrapper}>
                        {unselectedConnectorItems}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConnectorTypeFilter;
